import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Bar, Doughnut } from 'react-chartjs-2';
import StatCard from '../components/StatCard';
import MiniStatCard from '../components/MiniStatCard';
import CardSkeleton, { MiniCardSkeleton } from '../components/CardSkeleton';
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';
import currency from 'helpers/currency';
import { Tag, ShoppingBag, RotateCcw, DollarSign } from 'lucide-react';
import dashboardStatistics from 'api/requests/dashboardStatistics';

export default function IngressosTab({ periodParams }) {
    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (!periodParams) return;

        setIsLoading(true);
        dashboardStatistics.getIngressos(periodParams)
            .then(response => setData(response.data))
            .catch(error => console.error('Erro ao carregar ingressos:', error))
            .finally(() => setIsLoading(false));
    }, [periodParams]);

    if (isLoading) {
        return (
            <Container>
                <KpiGrid>
                    <MiniCardSkeleton />
                    <MiniCardSkeleton />
                    <MiniCardSkeleton />
                    <MiniCardSkeleton />
                </KpiGrid>
                <ChartsGrid>
                    <CardSkeleton height={340} />
                    <CardSkeleton height={340} />
                </ChartsGrid>
            </Container>
        );
    }

    if (!data) return null;

    const { ticketMedio, ticketMedioTrend, totalVendidos, totalVendidosTrend, totalEstornos, totalEstornosTrend, mediaPreco, mediaPrecotTrend, vendasPorMes, porCategoria } = data;

    const vendasMesChartData = {
        labels: vendasPorMes.labels,
        datasets: [
            {
                label: 'Ingressos vendidos',
                data: vendasPorMes.valores,
                backgroundColor: 'rgba(105, 12, 228, 0.7)',
                borderColor: '#690ce4',
                borderWidth: 1,
                borderRadius: 6,
                barPercentage: 0.6,
            },
            {
                label: 'Estornos',
                data: vendasPorMes.estornos,
                backgroundColor: 'rgba(231, 80, 90, 0.7)',
                borderColor: '#E7505A',
                borderWidth: 1,
                borderRadius: 6,
                barPercentage: 0.6,
            },
        ],
    };

    const vendasMesOptions = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            yAxes: [{
                ticks: {
                    beginAtZero: true,
                    fontColor: '#999',
                    fontSize: 12,
                },
                gridLines: { color: '#f0f0f0', drawBorder: false },
            }],
            xAxes: [{
                ticks: { fontColor: '#999', fontSize: 12 },
                gridLines: { display: false },
            }],
        },
        legend: {
            position: 'top',
            align: 'end',
            labels: { boxWidth: 12, fontSize: 12 },
        },
        tooltips: {
            backgroundColor: '#1A1A1A',
            cornerRadius: 8,
            xPadding: 12,
            yPadding: 10,
        },
    };

    const categoriaColors = ['#690ce4', '#4F86F7', '#2BC866', '#ff9123'];

    const categoriaChartData = {
        labels: porCategoria.map(c => c.nome),
        datasets: [{
            data: porCategoria.map(c => c.quantidade),
            backgroundColor: categoriaColors,
            borderWidth: 0,
            hoverBorderWidth: 2,
            hoverBorderColor: '#fff',
        }],
    };

    const categoriaOptions = {
        responsive: true,
        maintainAspectRatio: false,
        cutoutPercentage: 65,
        legend: { display: false },
        tooltips: {
            backgroundColor: '#1A1A1A',
            cornerRadius: 8,
            callbacks: {
                label: function (tooltipItem, data) {
                    const label = data.labels[tooltipItem.index];
                    const value = data.datasets[0].data[tooltipItem.index];
                    return ` ${label}: ${value.toLocaleString('pt-BR')}`;
                },
            },
        },
    };

    return (
        <Container>
            <KpiGrid>
                <MiniStatCard
                    label="Ticket Medio"
                    value={currency.format(ticketMedio)}
                    trend={ticketMedioTrend}
                    isPositive
                    icon={DollarSign}
                />
                <MiniStatCard
                    label="Total Vendidos"
                    value={totalVendidos.toLocaleString('pt-BR')}
                    trend={totalVendidosTrend}
                    isPositive
                    icon={ShoppingBag}
                />
                <MiniStatCard
                    label="Total Estornos"
                    value={totalEstornos.toLocaleString('pt-BR')}
                    trend={totalEstornosTrend}
                    isPositive={false}
                    icon={RotateCcw}
                />
                <MiniStatCard
                    label="Preco Medio"
                    value={currency.format(mediaPreco)}
                    trend={mediaPrecotTrend}
                    isPositive
                    icon={Tag}
                />
            </KpiGrid>

            <ChartsGrid>
                <StatCard title="Ingressos Vendidos por Mes" subtitle="Vendas e estornos mensais de ingressos">
                    <ChartContainer>
                        <Bar data={vendasMesChartData} options={vendasMesOptions} />
                    </ChartContainer>
                </StatCard>

                <StatCard title="Ingressos por Categoria" subtitle="Distribuicao por tipo de ingresso">
                    <CategoriaContent>
                        <DoughnutWrap>
                            <Doughnut data={categoriaChartData} options={categoriaOptions} />
                        </DoughnutWrap>
                        <CategoriaList>
                            {porCategoria.map((cat, i) => (
                                <CategoriaItem key={i}>
                                    <CategoriaLeft>
                                        <CatDot color={categoriaColors[i]} />
                                        <CatLabel>{cat.nome}</CatLabel>
                                    </CategoriaLeft>
                                    <CategoriaRight>
                                        <CatQuantity>{cat.quantidade.toLocaleString('pt-BR')}</CatQuantity>
                                        <CatPercent>{cat.percentual}%</CatPercent>
                                    </CategoriaRight>
                                </CategoriaItem>
                            ))}
                        </CategoriaList>
                    </CategoriaContent>
                </StatCard>
            </ChartsGrid>
        </Container>
    );
}

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
`;

const KpiGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;

    @media screen and (max-width: ${breakpoints.lg}) {
        grid-template-columns: repeat(2, 1fr);
    }

    @media screen and (max-width: ${breakpoints.sm}) {
        grid-template-columns: 1fr;
    }
`;

const ChartsGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;

    @media screen and (max-width: ${breakpoints.md}) {
        grid-template-columns: 1fr;
    }
`;

const ChartContainer = styled.div`
    height: 280px;
    width: 100%;
`;

const CategoriaContent = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 24px;
`;

const DoughnutWrap = styled.div`
    width: 180px;
    height: 180px;
`;

const CategoriaList = styled.div`
    width: 100%;
`;

const CategoriaItem = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;
    border-bottom: 1px solid #f5f5f5;

    &:last-child {
        border-bottom: none;
    }
`;

const CategoriaLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const CatDot = styled.div`
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${({ color }) => color};
`;

const CatLabel = styled.span`
    font-size: 14px;
    color: ${colors.nightDark};
`;

const CategoriaRight = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

const CatQuantity = styled.span`
    font-size: 14px;
    font-weight: 600;
    color: ${colors.nightDark};
`;

const CatPercent = styled.span`
    font-size: 13px;
    color: ${colors.night};
`;
